<template>
  <div>
    <template v-if="$slots.default">
      <label class="label">
        <span class="is-semi-bold font-sm">
          <slot />
        </span>
      </label>
    </template>

    <section
      :class="{
        'has-value': !!modelValue,
      }"
      class="multi-switch"
      :style="thumbStyle"
    >
      <span
        v-for="(opt, index) in options"
        :key="index"
        class="multi-switch__item"
        :class="{
          'is-active': modelValue === opt.value,
        }"
        @click="$emit('change', opt.value)"
      >
        {{ opt.label }}
      </span>
    </section>

    <!-- error -->
    <div
      v-if="errorMessage"
      class="error-message"
    >
      <small class="has-text-danger">{{ errorMessage }}</small>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DualSwitch',
  model: {
    prop: 'modelValue',
    event: 'change',
  },
  props: {
    options: {
      type: Array,
      required: true,
      validator: (opts) => opts.length >= 2 && opts.length <= 5,
    },
    modelValue: {
      type: [String, Number, Boolean],
      required: true,
    },
    errorMessage: {
      type: String,
      default: '',
      required: false,
    },
  },
  emits: ['change'],
  computed: {
    /** Index of the currently selected option */
    activeIndex() {
      return this.options.findIndex((opt) => opt.value === this.modelValue);
    },

    /**
     * Drives the sliding thumb via CSS custom properties so the
     * pseudo-element never needs to know the option count at compile time.
     *
     * --thumb-count : total slots  → sets each item's width (100% / N)
     * --thumb-offset: active index → translateX(index * 100%)
     */
    thumbStyle() {
      return {
        '--thumb-count': this.options.length,
        '--thumb-offset': this.activeIndex,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/_textVariables.scss';

.label {
  margin-bottom: 0;

  span {
    font-family: Inter;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #666;
    display: flex;
    align-items: center;
    gap: 4px;
  }
}

.error-message {
  height: 20px;
}

.multi-switch {
  position: relative;
  display: flex;
  width: 100%;
  height: 42px;
  padding: 2px;
  border-radius: 6px;
  gap: 2px;
  background-color: $slate-200;

  // Sliding thumb — width and position driven by CSS custom properties
  &.has-value {
    &::before {
      content: '';
      position: absolute;
      top: 2px;
      left: 2px;

      // Width = one slot, accounting for the 2px left offset
      width: calc((100% - 4px) / var(--thumb-count));
      height: calc(100% - 4px);

      background: white;
      border-radius: 6px;
      border: 1px solid $gray-200;
      box-shadow: 0px 1px 2px 0px #0000000d;
      will-change: transform;
      transition: transform 0.25s ease-in-out;

      // Each step = exactly one slot width (no pixel magic needed)
      transform: translateX(calc(var(--thumb-offset) * 100%));
    }
  }

  &__item {
    flex: 1;

    position: relative;
    margin: auto;

    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 100%;

    font: 400 16px 'Inter';
    text-align: center;

    cursor: pointer;
    will-change: color;
    transition: color 0.25s cubic-bezier(0.45, 0.05, 0.55, 0.95);
    user-select: none;

    &.is-active {
      font-weight: 700;
    }
  }
}
</style>
